import React, { useState } from 'react';
import { adminAPI } from '../../services/api';
import { Download, Database, FileText, Calendar } from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner';
import toast from 'react-hot-toast';

const DataExport = () => {
  const [exportingTable, setExportingTable] = useState(null);

  const tables = [
    {
      name: 'products',
      label: 'Products',
      description: 'All product data including names, descriptions, and categories',
      icon: Database
    },
    {
      name: 'product_variants',
      label: 'Product Variants',
      description: 'Product variants with pricing and API endpoint information',
      icon: Database
    },
    {
      name: 'orders',
      label: 'Orders',
      description: 'Complete order history with customer data and API responses',
      icon: FileText
    },
    {
      name: 'api_configs',
      label: 'API Configurations',
      description: 'API endpoint configurations (sensitive data will be masked)',
      icon: Database
    },
    {
      name: 'settings',
      label: 'System Settings',
      description: 'System configuration and settings',
      icon: Database
    }
  ];

  const handleExport = async (tableName) => {
    setExportingTable(tableName);
    try {
      const response = await adminAPI.exportData(tableName);
      const data = response.data.data;
      
      // Create and download JSON file
      const jsonString = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `${tableName}_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`${data.count} records exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${tableName}`);
    } finally {
      setExportingTable(null);
    }
  };

  const handleExportAll = async () => {
    setExportingTable('all');
    try {
      const exports = {};
      
      for (const table of tables) {
        const response = await adminAPI.exportData(table.name);
        exports[table.name] = response.data.data;
      }
      
      // Create comprehensive export
      const exportData = {
        exportDate: new Date().toISOString(),
        version: '1.0.0',
        tables: exports
      };
      
      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `complete_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Complete backup exported successfully');
    } catch (error) {
      toast.error('Failed to export complete backup');
    } finally {
      setExportingTable(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-medium text-gray-900">Data Export</h2>
        <p className="mt-1 text-sm text-gray-500">
          Export system data for backup or migration purposes
        </p>
      </div>

      {/* Export All Button */}
      <div className="card">
        <div className="card-content">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Complete Backup</h3>
              <p className="text-sm text-gray-500">
                Export all system data in a single comprehensive backup file
              </p>
            </div>
            <button
              onClick={handleExportAll}
              disabled={exportingTable === 'all'}
              className="btn btn-primary"
            >
              {exportingTable === 'all' ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Export All Data
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Individual Table Exports */}
      <div className="grid gap-6 md:grid-cols-2">
        {tables.map((table) => {
          const Icon = table.icon;
          return (
            <div key={table.name} className="card">
              <div className="card-content">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <Icon className="h-8 w-8 text-primary-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">
                        {table.label}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {table.description}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4">
                  <button
                    onClick={() => handleExport(table.name)}
                    disabled={exportingTable === table.name}
                    className="btn btn-outline w-full"
                  >
                    {exportingTable === table.name ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        Export {table.label}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Export Information */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Export Information</h3>
        </div>
        <div className="card-content">
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-900">File Format</h4>
              <p className="text-sm text-gray-500">
                All exports are in JSON format for easy import and processing.
              </p>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-gray-900">Data Security</h4>
              <p className="text-sm text-gray-500">
                Sensitive information like API keys and passwords are masked in exports.
                Complete backups should be stored securely.
              </p>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-gray-900">File Naming</h4>
              <p className="text-sm text-gray-500">
                Export files are named with the table name and current date for easy identification.
              </p>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-gray-900">Usage</h4>
              <ul className="text-sm text-gray-500 list-disc list-inside space-y-1">
                <li>Use individual exports for specific data analysis</li>
                <li>Use complete backup for system migration or disaster recovery</li>
                <li>Regular backups are recommended for production systems</li>
                <li>Test import procedures in development environments</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Best Practices */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="flex">
          <Calendar className="h-5 w-5 text-blue-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Backup Best Practices
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Schedule regular automated backups</li>
                <li>Store backups in multiple secure locations</li>
                <li>Test backup restoration procedures regularly</li>
                <li>Keep backups for compliance and audit requirements</li>
                <li>Document backup and recovery procedures</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataExport;
